"use client";

/**
 * ReflectBalanceHero — top-of-page balance summary for Reflect.
 *
 * Three summary stats divided by vertical rules:
 *   1. "X.X / Yh"   total scheduled / total committed (forest)
 *   2. "N"          areas with target>0 && scheduled=0  (amber)
 *   3. "N"          areas where ratio ≥ 0.8             (forest/sage)
 *
 * Plus a per-area list. Each row collapses to icon + name + bar + stats;
 * tapping expands a goals sub-list filtered from `listGoals` by
 * `goal.category === area.area`. The "Adjust commitments" CTA opens the
 * existing Decide › Balance modal (delegated to host via onAdjustClick).
 */

import { useMemo, useState } from "react";
import { AreaIcon, areaKeyToIcon, fmt } from "@/components/balance/BalanceList";
import type { GoalItem } from "@/lib/api";
import type { ReflectAllocationArea } from "@/lib/api/projection-types";
import styles from "./reflect-page.module.css";

export interface ReflectBalanceHeroProps {
  areas: ReflectAllocationArea[];
  /** All active goals; used to derive per-area expansion lists. */
  goals: GoalItem[];
  /** Fired when user taps "Adjust commitments". Host navigates / opens modal. */
  onAdjustClick: () => void;
}

interface AreaGoalSlim {
  id: string;
  name: string;
  /** Per-goal target minutes derived from API (best-effort; 0 if unknown). */
  target: number;
  /** Per-goal scheduled minutes derived from API. */
  sched: number;
  /** "amber" when sched=0 && target>0; otherwise "ok". */
  status: "amber" | "ok";
}

function summarize(areas: ReflectAllocationArea[]) {
  const totalTarget = areas.reduce(
    (s, a) => s + (a.target_minutes_per_week ?? 0),
    0,
  );
  const totalSched = areas.reduce((s, a) => s + (a.scheduled_minutes ?? 0), 0);
  const attention = areas.filter(
    (a) =>
      (a.target_minutes_per_week ?? 0) > 0 && (a.scheduled_minutes ?? 0) === 0,
  ).length;
  const onTrack = areas.filter((a) => {
    const t = a.target_minutes_per_week ?? 0;
    if (t === 0) return false;
    return (a.scheduled_minutes ?? 0) >= t * 0.8;
  }).length;
  return { totalTarget, totalSched, attention, onTrack };
}

function goalsForArea(goals: GoalItem[], areaKey: string): AreaGoalSlim[] {
  // The backend's GoalItem doesn't currently expose per-goal target /
  // scheduled minutes. We surface what we have (title) and leave numbers
  // at 0 until the API ships per-goal allocation. Status flips to "amber"
  // for goals with no progress at all.
  return goals
    .filter((g) => (g.category ?? "").toLowerCase() === areaKey.toLowerCase())
    .map((g) => ({
      id: g.id,
      name: g.title,
      target: 0,
      sched: 0,
      status:
        (g.progress_pct ?? 0) === 0 && g.status === "active"
          ? ("amber" as const)
          : ("ok" as const),
    }));
}

function ChevronIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

function ArrowIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

export default function ReflectBalanceHero({
  areas,
  goals,
  onAdjustClick,
}: ReflectBalanceHeroProps) {
  const [openId, setOpenId] = useState<string | null>(null);

  const { totalTarget, totalSched, attention, onTrack } = useMemo(
    () => summarize(areas),
    [areas],
  );

  if (areas.length === 0) {
    return (
      <section className={styles["rf-balance"]}>
        <header className={styles["rf-balance-head"]}>
          <div className={styles["rf-balance-summary"]}>
            <div className={styles["rf-balance-stat"]}>
              <div className={styles["rf-balance-stat-num"]}>—</div>
              <div className={styles["rf-balance-stat-label"]}>
                No commitments yet
              </div>
            </div>
          </div>
          <button
            type="button"
            className={styles["rf-balance-cta"]}
            onClick={onAdjustClick}
          >
            Set commitments <ArrowIcon size={14} />
          </button>
        </header>
        <p
          style={{
            margin: 0,
            paddingTop: 12,
            fontSize: 13,
            color: "var(--textLight)",
          }}
        >
          Pick a few life areas in Decide › Balance to start tracking the gap
          between what you promised and what you scheduled.
        </p>
      </section>
    );
  }

  return (
    <section className={styles["rf-balance"]}>
      <header className={styles["rf-balance-head"]}>
        <div className={styles["rf-balance-summary"]}>
          <div className={styles["rf-balance-stat"]}>
            <div className={styles["rf-balance-stat-num"]}>
              {(totalSched / 60).toFixed(1)}
              <small>/{Math.round(totalTarget / 60)}h</small>
            </div>
            <div className={styles["rf-balance-stat-label"]}>
              Scheduled this week
            </div>
          </div>
          <div className={styles["rf-balance-divider"]} />
          <div
            className={`${styles["rf-balance-stat"]} ${styles["is-amber"]}`}
          >
            <div className={styles["rf-balance-stat-num"]}>{attention}</div>
            <div className={styles["rf-balance-stat-label"]}>
              Areas need attention
            </div>
          </div>
          <div className={styles["rf-balance-divider"]} />
          <div className={`${styles["rf-balance-stat"]} ${styles["is-sage"]}`}>
            <div className={styles["rf-balance-stat-num"]}>{onTrack}</div>
            <div className={styles["rf-balance-stat-label"]}>On track</div>
          </div>
        </div>
        <button
          type="button"
          className={styles["rf-balance-cta"]}
          onClick={onAdjustClick}
          data-testid="reflect-balance-adjust"
        >
          Adjust commitments <ArrowIcon size={14} />
        </button>
      </header>

      <ol className={styles["rf-areas"]}>
        {areas.map((a) => {
          const sched = a.scheduled_minutes ?? 0;
          const target = a.target_minutes_per_week ?? 0;
          const isNull = target === 0;
          const ratio = isNull ? 0 : sched / target;
          const isAttn = !isNull && sched === 0;
          const isOk = !isNull && ratio >= 0.8;
          const isOpen = openId === a.area;

          const stateClasses = [
            styles["rf-area"],
            isNull ? styles["is-null"] : "",
            isAttn ? styles["is-attn"] : "",
            isOk ? styles["is-ok"] : "",
            isOpen ? styles["is-open"] : "",
          ]
            .filter(Boolean)
            .join(" ");

          const areaGoals = goalsForArea(goals, a.area);
          const stateText = isNull
            ? "no commitment"
            : isAttn
              ? "attention"
              : isOk
                ? "on track"
                : `${Math.round(ratio * 100)}%`;

          return (
            <li
              key={a.area}
              className={stateClasses}
              data-testid={`reflect-balance-area-${a.area}`}
            >
              <button
                type="button"
                className={styles["rf-area-row"]}
                onClick={() => setOpenId(isOpen ? null : a.area)}
                aria-expanded={isOpen}
                aria-controls={`rf-goals-${a.area}`}
              >
                <span className={styles["rf-area-icon"]} aria-hidden>
                  <AreaIcon kind={areaKeyToIcon(a.area)} size={16} />
                </span>
                <span className={styles["rf-area-name"]}>
                  {a.label}
                  {areaGoals.length > 0 && (
                    <span className={styles["rf-area-goalcount"]}>
                      {areaGoals.length} goal
                      {areaGoals.length === 1 ? "" : "s"}
                    </span>
                  )}
                </span>
                <span className={styles["rf-area-bar"]} aria-hidden>
                  <span
                    className={styles["rf-area-bar-fill"]}
                    style={{ width: `${Math.min(100, ratio * 100)}%` }}
                  />
                </span>
                <span>
                  <span className={styles["rf-area-stats"]}>
                    <strong>{fmt(sched)}</strong> / {fmt(target || null)}
                  </span>
                  <div className={styles["rf-area-stats-pct"]}>{stateText}</div>
                </span>
                <span className={styles["rf-area-chev"]} aria-hidden>
                  <ChevronIcon size={14} />
                </span>
              </button>

              {isOpen && areaGoals.length > 0 && (
                <ul
                  className={styles["rf-goals"]}
                  id={`rf-goals-${a.area}`}
                >
                  {areaGoals.map((g) => {
                    const gRatio = g.target > 0 ? g.sched / g.target : 0;
                    return (
                      <li
                        key={g.id}
                        className={`${styles["rf-goal"]} ${
                          g.status === "amber" ? styles["is-amber"] : ""
                        }`}
                      >
                        <div className={styles["rf-goal-name"]}>{g.name}</div>
                        <div className={styles["rf-goal-bar"]} aria-hidden>
                          <div
                            className={styles["rf-goal-bar-fill"]}
                            style={{ width: `${Math.min(100, gRatio * 100)}%` }}
                          />
                        </div>
                        <div className={styles["rf-goal-stat"]}>
                          {g.target > 0
                            ? `${fmt(g.sched)} / ${fmt(g.target)}`
                            : "—"}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              {isOpen && areaGoals.length === 0 && (
                <p
                  style={{
                    margin: "0 0 12px 70px",
                    fontSize: 12,
                    color: "var(--textLight)",
                    fontStyle: "italic",
                  }}
                >
                  No active goals linked to this area yet.
                </p>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
